import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCanvas } from "../../../../context/CanvasContext";
import ConfirmationModal from "../../../../../shared/components/modals/ConfirmationModal";
import EditModal from "../../../../../shared/components/modals/EditModal";
import InstructionsModal from "../../../../../shared/components/InstructionsModal";
import GenericDetailsModal from "../../../../../shared/components/modals/GenericDetailsModal";
import GenericCard from "../../../../../shared/components/GenericCard";
import { toast } from "react-toastify";
import {
  fetchDebts,
  createDebt,
  updateDebt,
  deleteDebt,
  uploadDebts,
  selectDebts,
  selectDebtLoading,
  selectDebtPagination,
  debtOverview,
  selectDebtOverview,
} from "../../../../../../app/store/slices/debtSlice";
import { selectAccounts, fetchAccounts } from "../../../../../../app/store/slices/accountSlice";
import Pagination from "../../../../../shared/components/Pagination";
import {DEBT_TYPE_OPTIONS, DEBT_STATUS_OPTIONS } from "../../../../../../utils/constants/dashboardConstants";
import FileUploadButton from "../../../../../shared/components/FileUploadButton";
import FormInput from "../../../../../shared/ui/FormInput";
import FormSelect from "../../../../../shared/ui/FormSelect";
import FormTextarea from "../../../../../shared/ui/FormTextarea";
import useClickOutside from "../../../../../shared/hooks/useClickOutside";
import Spinner from '../../../../../shared/components/Spinner';
import SummaryCard from "../../../../../shared/components/SummaryCard";
import { FiTrendingUp, FiPercent } from "react-icons/fi";
import AddOptionsButton from "../../../../../shared/components/AddOptionsButton";
import { FcDebt } from "react-icons/fc";
import {formatAmount} from '../../../../../../utils/formatAmount';
import { 
  calculateNextDueDate, 
  getSchedulePreviewText, 
  dueDateTypes, 
  months, 
  monthDays,
  shouldShowDay,
  shouldShowMonth,
  getCurrentMonth 
} from '../../../../../../utils/dueDateCalculator';

const DebtContent = () => {
  const dispatch = useDispatch();
  const { selectedCanvas } = useCanvas();
  const debts = useSelector(selectDebts);
  const loading = useSelector(selectDebtLoading);
  const pagination = useSelector(selectDebtPagination);
  const accounts = useSelector(selectAccounts);
  const overview = useSelector(selectDebtOverview);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);

  // Modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Data states
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [editDebtData, setEditDebtData] = useState({
    title: "",
    type: "",
    initialAmount: "",
    annualInterestRate: "",
    startDate: "",
    dueDate: "",
    amountRepaid: "",
    monthlyPayment: "",
    monthlyPaymentDueDate: "",
    status: "active",
    paymentAccountId: "",
    notes: "",
    paymentDueDateType: "",
    paymentDueDateDay: "",
    paymentDueDateMonth: ""
  });
  const [addDebtData, setAddDebtData] = useState({
    title: "",
    type: "",
    initialAmount: "",
    annualInterestRate: "",
    startDate: "",
    dueDate: "",
    amountRepaid: "",
    monthlyPayment: "",
    monthlyPaymentDueDate: "",
    status: "active",
    paymentAccountId: "",
    notes: "",
    paymentDueDateType: "",
    paymentDueDateDay: "",
    paymentDueDateMonth: ""
  });

  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const fileInputRef = useRef();
  const addOptionsRef = useRef(null);
  const baseCurrency = selectedCanvas?.currency || "";
  
  useClickOutside(addOptionsRef, () => setShowAddOptions(false), showAddOptions);

  // Format currency
  const formatCurrency = (amount) => {
    return `${baseCurrency} ${formatAmount(amount)}`;
  };

  useEffect(() => {
    if (addDebtData.paymentDueDateType && addDebtData.paymentDueDateMonth) {
      const calculatedDate = calculateNextDueDate(addDebtData.paymentDueDateType, addDebtData.paymentDueDateMonth, addDebtData.paymentDueDateDay);
      if (calculatedDate && calculatedDate !== addDebtData.monthlyPaymentDueDate) {
        setAddDebtData(prev => ({ ...prev, monthlyPaymentDueDate: calculatedDate }));
      }
    }
  }, [addDebtData.paymentDueDateType, addDebtData.paymentDueDateMonth, addDebtData.paymentDueDateDay]);

  useEffect(() => {
    if (editDebtData.paymentDueDateType && editDebtData.paymentDueDateMonth) {
      const calculatedDate = calculateNextDueDate(editDebtData.paymentDueDateType, editDebtData.paymentDueDateMonth, editDebtData.paymentDueDateDay);
      if (calculatedDate && calculatedDate !== editDebtData.monthlyPaymentDueDate) {
        setEditDebtData(prev => ({ ...prev, monthlyPaymentDueDate: calculatedDate }));
      }
    }
  }, [editDebtData.paymentDueDateType, editDebtData.paymentDueDateMonth, editDebtData.paymentDueDateDay]);

  useEffect(() => {
    if (addDebtData.paymentDueDateType && !addDebtData.paymentDueDateMonth) {
      setAddDebtData(prev => ({ ...prev, paymentDueDateMonth: getCurrentMonth() }));
    }
  }, [addDebtData.paymentDueDateType]);

  useEffect(() => {
    if (editDebtData.paymentDueDateType && !editDebtData.paymentDueDateMonth) {
      setEditDebtData(prev => ({ ...prev, paymentDueDateMonth: getCurrentMonth() }));
    }
  }, [editDebtData.paymentDueDateType]);

  // File upload template download
  const handleDownloadTemplate = () => {
    try {
      const sampleAccountName = accounts.length > 0 ? accounts[0].name : 'account_name_here';
      
      const templateData = [
        { 
          title: 'Car Loan', 
          type: 'General Debt',
          initialAmount: '25000', 
          annualInterestRate: '5.5',
          startDate: '2024-01-01',
          dueDate: '2029-01-01',
          amountRepaid: '5000', 
          monthlyPayment: '450',
          monthlyPaymentDueDate: '15',
          status: 'active',
          paymentAccountId: sampleAccountName,
          notes: 'Car loan for Honda Civic'
        },
        { 
          title: 'Credit Card', 
          type: 'credit_card',
          initialAmount: '8000', 
          annualInterestRate: '18.9',
          startDate: '2023-06-01',
          dueDate: '2026-06-01',
          amountRepaid: '2000', 
          monthlyPayment: '300',
          monthlyPaymentDueDate: '5',
          status: 'active',
          paymentAccountId: sampleAccountName,
          notes: 'High interest credit card debt'
        },
        { 
          title: 'Student Loan', 
          type: 'student',
          initialAmount: '35000', 
          annualInterestRate: '4.2',
          startDate: '2020-09-01',
          dueDate: '2030-09-01',
          amountRepaid: '12000', 
          monthlyPayment: '350',
          monthlyPaymentDueDate: '1',
          status: 'active',
          paymentAccountId: sampleAccountName,
          notes: 'Federal student loan'
        }
      ];

      const csvContent = [
        'title,type,initialAmount,annualInterestRate,startDate,dueDate,amountRepaid,monthlyPayment,monthlyPaymentDueDate,status,paymentAccountId,notes',
        ...templateData.map(row => 
          `${row.title},${row.type},${row.initialAmount},${row.annualInterestRate},${row.startDate},${row.dueDate},${row.amountRepaid},${row.monthlyPayment},${row.monthlyPaymentDueDate},${row.status},${row.paymentAccountId},"${row.notes}"`
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debts_template_${baseCurrency}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setShowInstructions(false);
    } catch (error) {
      toast.error('Failed to download template');
      console.error('Template download error:', error);
    }
  };

  useEffect(() => {
    if (selectedCanvas?.id) {
      dispatch(fetchDebts({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(fetchAccounts({ canvasId: selectedCanvas.id, limit:1000 }));
      dispatch(debtOverview({ canvasId: selectedCanvas.id }));
    }
  }, [dispatch, selectedCanvas?.id, currentPage, pageSize]);

  // Autofill edit form
  useEffect(() => {
    if (showEditForm && selectedDebt) {
      setEditDebtData({
        title: selectedDebt.title || "",
        type: selectedDebt.type || "",
        initialAmount: selectedDebt.initialAmount ?? "",
        annualInterestRate: selectedDebt.annualInterestRate ?? "",
        startDate: selectedDebt.startDate ? selectedDebt.startDate.slice(0, 10) : "",
        dueDate: selectedDebt.dueDate ? selectedDebt.dueDate.slice(0, 10) : "",
        amountRepaid: selectedDebt.amountRepaid ?? "",
        monthlyPayment: selectedDebt.monthlyPayment ?? "",
        monthlyPaymentDueDate: selectedDebt.monthlyPaymentDueDate ? selectedDebt.monthlyPaymentDueDate.slice(0, 10) : "",
        status: selectedDebt.status || "active",
        paymentAccountId: selectedDebt.paymentAccountId || "",
        notes: selectedDebt.notes || "",
        paymentDueDateType: selectedDebt.paymentDueDateType || "",
        paymentDueDateDay: selectedDebt.paymentDueDateDay || "",
        paymentDueDateMonth: selectedDebt.paymentDueDateMonth || ""
      });
    }
  }, [showEditForm, selectedDebt]);

  // Handle Delete
  const handleDeleteDebt = async () => {
    if (!selectedDebt || !selectedCanvas?.id) {
      toast.error("Invalid Canvas or Debt");
      return;
    }
    setIsDeleting(true);
    try {
      await dispatch(
        deleteDebt({
          canvasId: selectedCanvas.id,
          debtId: selectedDebt._id,
        })
      ).unwrap();
      setShowDeleteConfirm(false);
      setSelectedDebt(null);
      dispatch(fetchDebts({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(debtOverview({ canvasId: selectedCanvas.id }));
      toast.success("Debt Deleted Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle Edit
  const handleEditDebt = async (e) => {
    e.preventDefault();
    if (!selectedDebt || !selectedCanvas?.id) {
      toast.error("Invalid Canvas or Debt");
      return;
    }
    setIsEditing(true);
    try {
      await dispatch(
        updateDebt({
          canvasId: selectedCanvas.id,
          debtId: selectedDebt._id,
          debtData: {
            ...editDebtData,
            initialAmount: Number(editDebtData.initialAmount),
            annualInterestRate: Number(editDebtData.annualInterestRate),
            amountRepaid: Number(Number(editDebtData.amountRepaid).toFixed(2)),
            monthlyPayment: Number(editDebtData.monthlyPayment),
            paymentDueDateType: editDebtData.paymentDueDateType,
            paymentDueDateDay: editDebtData.paymentDueDateDay,
            paymentDueDateMonth: editDebtData.paymentDueDateMonth
          },
        })
      ).unwrap();
      setShowEditForm(false);
      setSelectedDebt(null);
      dispatch(fetchDebts({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(debtOverview({ canvasId: selectedCanvas.id }));
      toast.success("Debt Updated Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsEditing(false);
    }
  };

  // Handle Add
  const handleAddDebt = async (e) => {
    e.preventDefault();
    if (!selectedCanvas?.id) {
      toast.error("Invalid Canvas");
      return;
    }
    setIsAdding(true);
    try {
      await dispatch(
        createDebt({
          canvasId: selectedCanvas.id,
          debtData: {
            ...addDebtData,
            initialAmount: Number(addDebtData.initialAmount),
            annualInterestRate: Number(addDebtData.annualInterestRate),
            amountRepaid: Number(Number(addDebtData.amountRepaid || 0).toFixed(2)),
            monthlyPayment: Number(addDebtData.monthlyPayment),
            paymentDueDateType: addDebtData.paymentDueDateType,
            paymentDueDateDay: addDebtData.paymentDueDateDay,
            paymentDueDateMonth: addDebtData.paymentDueDateMonth
          },
        })
      ).unwrap();
      setShowAddForm(false);
      setAddDebtData({
        title: "",
        type: "",
        initialAmount: "",
        annualInterestRate: "",
        startDate: "",
        dueDate: "",
        amountRepaid: "",
        monthlyPayment: "",
        monthlyPaymentDueDate: "",
        status: "active",
        paymentAccountId: "",
        notes: "",
        paymentDueDateType: "",
        paymentDueDateDay: "",
        paymentDueDateMonth: ""
      });
      dispatch(fetchDebts({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(debtOverview({ canvasId: selectedCanvas.id }));
      toast.success("Debt Created Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdateDebt = async (debtId, updateData) => {
    setIsUpdating(true);
    try {
      await dispatch(
        updateDebt({
          canvasId: selectedCanvas.id,
          debtId,
          debtData: updateData,
        })
      ).unwrap();
      
      setSelectedDebt(prev => ({ ...prev, ...updateData }));
      
      dispatch(fetchDebts({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(debtOverview({ canvasId: selectedCanvas.id }));
      toast.success("Debt updated successfully");
    } catch (err) {
      toast.error(err?.message || 'Failed to update debt');
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddPayment = async (debtId, amount) => {
    try {
      const debt = debts.find(d => d._id === debtId);
      if (!debt) {
        toast.error("Debt not found");
        return;
      }

      const newAmountRepaid = (debt.amountRepaid || 0) + amount;
      const newBalance = debt.initialAmount - newAmountRepaid;
      
      const updateData = { 
        amountRepaid: newAmountRepaid,
        balance: newBalance 
      };
      
      if (newBalance <= 0 && debt.status !== 'completed') {
        updateData.status = 'completed';
      }
      
      await handleUpdateDebt(debtId, updateData);
      
      const statusMessage = newBalance <= 0 && debt.status !== 'completed' 
        ? ` - Congratulations! Your debt is now fully paid! 🎉`
        : '';
      dispatch(fetchDebts({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(debtOverview({ canvasId: selectedCanvas.id }));
      toast.success(`Payment of ${formatCurrency(amount)} recorded successfully${statusMessage}`);
    } catch (err) {
      toast.error('Failed to record payment');
      throw err;
    }
  };

  // Handle file upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCanvas?.id) {
      toast.error("Invalid Canvas ID or file");
      return;
    }
    try {
      await dispatch(uploadDebts({ canvasId: selectedCanvas.id, file })).unwrap();
      dispatch(fetchDebts({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(debtOverview({ canvasId: selectedCanvas.id }));
      toast.success("Debts Imported Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    }
  };

  // Transform debt data fr GenericCard component
  const transformDebtForCard = (debt) => ({
    ...debt,
    currentAmount: debt.amountRepaid || 0,
    balance: debt.initialAmount - (debt.amountRepaid || 0),
    estimatedFinalPaymentDate: debt.estimatedFinalPaymentDate || debt.dueDate,
  });

  const PaymentScheduleForm = ({ data, setData, prefix = "" }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Payment Due Type
          </label>
          <select
            value={data.paymentDueDateType}
            onChange={(e) => setData({ ...data, paymentDueDateType: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
          >
            <option value="">Select Type</option>
            {dueDateTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {shouldShowMonth(data.paymentDueDateType) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Starting Month
            </label>
            <select
              value={data.paymentDueDateMonth || ''}
              onChange={(e) => setData({ ...data, paymentDueDateMonth: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
            >
              <option value="">Select Month</option>
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {shouldShowDay(data.paymentDueDateType) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Day
            </label>
            <select
              value={data.paymentDueDateDay || ''}
              onChange={(e) => setData({ ...data, paymentDueDateDay: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
            >
              <option value="">Select Day</option>
              {monthDays.map(day => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {(data.paymentDueDateType && data.paymentDueDateMonth) && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-800">
            <span className="font-medium">Payment Schedule Preview:</span>
            {getSchedulePreviewText(data.paymentDueDateType, data.paymentDueDateMonth, data.paymentDueDateDay)}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-xl md:text-2xl font-bold text-center mb-4 md:mb-6">Debts</h1>
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
      
      {/* Overview */}
      <div className="flex flex-wrap items-center justify-center md:justify-between gap-4 md:gap-8 mb-6">
        <div className="flex flex-wrap gap-4 md:gap-8 w-full md:w-auto">
          <SummaryCard
            title="Total Debt"
            value={overview?.remainingDebtAmount !== undefined ? overview.remainingDebtAmount : "Auto calculated"}
            icon={<FcDebt className="w-5 h-5" />}
            valueColor="text-red-700"
            iconBg="bg-red-100"
            iconColor="text-red-600"
            formatValue={formatCurrency}
          />
          <SummaryCard
            title="Total Monthly Payments"
            value={overview?.totalMonthlyPayments !== undefined ? overview.totalMonthlyPayments : "Auto calculated"}
            icon={<FiTrendingUp className="w-5 h-5" />}
            valueColor="text-btn-primary"
            iconBg="bg-blue-100"
            iconColor="text-btn-primary"
            formatValue={val => typeof val === "number" ? `${val}` : val}
          />
          <SummaryCard
            title="Debt Payment Rate"
            value={overview?.debtPayoffRate !== undefined ? overview.debtPayoffRate : "Auto calculated"}
            icon={<FiPercent className="w-5 h-5" />}
            valueColor="text-btn-primary"
            iconBg="bg-blue-100"
            iconColor="text-btn-primary"
            formatValue={val => typeof val === "number" ? `${val}%` : val}
          />
        </div>
        <AddOptionsButton
            showOptions={showAddOptions}
            setShowOptions={setShowAddOptions}
            addOptionsRef={addOptionsRef}
            onAddManual={() => setShowAddForm(true)}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            onShowInstructions={() => setShowInstructions(true)}
            buttonLabel="Record Debt"
            buttonTitle="Add Debt"
            manualLabel="Add Manually"
            FileUploadButtonComponent={FileUploadButton}
          />
      </div>

      {/* Debts Cards Grid */}
      
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Spinner text = {'Loading Debts...'} size ={'large'}/>
          </div>
        ) : debts?.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {debts.map(debt => (
                <GenericCard
                  key={debt._id}
                  item={transformDebtForCard(debt)}
                  baseCurrency={baseCurrency}
                  cardType="debt"
                  onEdit={() => {
                    setSelectedDebt(debt);
                    setShowEditForm(true);
                  }}
                  onDelete={() => {
                    setSelectedDebt(debt);
                    setShowDeleteConfirm(true);
                  }}
                  onViewDetails={() => {
                    setSelectedDebt(transformDebtForCard(debt));
                    setShowDetailsModal(true);
                  }}
                />
              ))}
            </div>
            
            {/* Pagination */}
            <Pagination
              pagination={pagination}
              pageSize={pageSize}
              setPageSize={setPageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              PAGE_SIZE_OPTIONS={[9, 18, 27]}
              label="Debts per page"
              totalLabel={`Total: ${pagination?.total || 0} debts`}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <FcDebt className='w-16 h-16 mx-auto'/>
            </div>
            <p className="text-gray-600 text-lg mb-2">No Debts Found</p>
            <p className="text-gray-500">Start tracking your debts by adding your first debt record.</p>
          </div>
        )}
      </div>
  
      {/* Modals */}
      <ConfirmationModal
        show={showDeleteConfirm}
        title="Delete Debt"
        message={`Are you sure you want to delete "${selectedDebt?.title}"? This action cannot be undone.`}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setSelectedDebt(null);
        }}
        onConfirm={handleDeleteDebt}
        loading={isDeleting}
      />

      <EditModal
        show={showEditForm}
        title="Edit Debt"
        onCancel={() => {
          setShowEditForm(false);
          setSelectedDebt(null);
        }}
        onSubmit={handleEditDebt}
        loading={isEditing}
        submitLabel="Update"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          <FormInput
            label="Title"
            type="text"
            value={editDebtData.title}
            onChange={e => setEditDebtData({ ...editDebtData, title: e.target.value })}
            required
          />
          <FormSelect
            label="Type"
            value={editDebtData.type}
            onChange={e => setEditDebtData({ ...editDebtData, type: e.target.value })}
            required
          >
            <option value="">Select Type</option>
            {DEBT_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormInput
            label="Initial Amount"
            type="number"
            value={editDebtData.initialAmount}
            onChange={e => setEditDebtData({ ...editDebtData, initialAmount: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Annual Interest Rate (%)"
            type="number"
            value={editDebtData.annualInterestRate}
            onChange={e => setEditDebtData({ ...editDebtData, annualInterestRate: e.target.value })}
            min="0"
            max="100"
            step="0.01"
            required
          />
          <FormInput
            label="Start Date"
            type="date"
            value={editDebtData.startDate}
            onChange={e => setEditDebtData({ ...editDebtData, startDate: e.target.value })}
            required
          />
          <FormInput
            label="Due Date"
            type="date"
            value={editDebtData.dueDate}
            onChange={e => setEditDebtData({ ...editDebtData, dueDate: e.target.value })}
            required
          />
          <FormInput
            label="Amount Repaid"
            type="number"
            value={editDebtData.amountRepaid}
            onChange={e => setEditDebtData({ ...editDebtData, amountRepaid: e.target.value })}
            min="0"
            step="0.01"
          />
          <FormInput
            label="Monthly Payment"
            type="number"
            value={editDebtData.monthlyPayment}
            onChange={e => setEditDebtData({ ...editDebtData, monthlyPayment: e.target.value })}
            min="0"
            step="0.01"
          />
          
          {/* Payment Schedule Form for Edit */}
          <PaymentScheduleForm 
            data={editDebtData} 
            setData={setEditDebtData}
          />
          
          {/* Auto-populated Monthly Payment Due Date Field */}
          {/* <FormInput
            label="Monthly Payment Due Date"
            type="date"
            value={editDebtData.monthlyPaymentDueDate}
            onChange={e => setEditDebtData({ ...editDebtData, monthlyPaymentDueDate: e.target.value })}
            className="bg-gray-50"
          /> */}
          
          <FormSelect
            label="Status"
            value={editDebtData.status}
            onChange={e => setEditDebtData({ ...editDebtData, status: e.target.value })}
            required
          >
            <option value="">Select Status</option>
            {DEBT_STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Payment Account"
            value={editDebtData.paymentAccountId}
            onChange={e => setEditDebtData({ ...editDebtData, paymentAccountId: e.target.value })}
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name}</option>
            ))}
          </FormSelect>
          <FormTextarea
            label="Notes"
            value={editDebtData.notes}
            onChange={e => setEditDebtData({ ...editDebtData, notes: e.target.value })}
            rows={2}
          />
        </div>
      </EditModal>

      <EditModal
        show={showAddForm}
        title="Create Debt"
        onCancel={() => setShowAddForm(false)}
        onSubmit={handleAddDebt}
        loading={isAdding}
        submitLabel="Create"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          <FormInput
            label="Title"
            type="text"
            value={addDebtData.title}
            onChange={e => setAddDebtData({ ...addDebtData, title: e.target.value })}
            required
          />
          <FormSelect
            label="Type"
            value={addDebtData.type}
            onChange={e => setAddDebtData({ ...addDebtData, type: e.target.value })}
            required
          >
            <option value="">Select Type</option>
            {DEBT_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormInput
            label="Initial Amount"
            type="number"
            value={addDebtData.initialAmount}
            onChange={e => setAddDebtData({ ...addDebtData, initialAmount: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Annual Interest Rate (%)"
            type="number"
            value={addDebtData.annualInterestRate}
            onChange={e => setAddDebtData({ ...addDebtData, annualInterestRate: e.target.value })}
            min="0"
            max="100"
            step="0.01"
            required
          />
          <FormInput
            label="Start Date"
            type="date"
            value={addDebtData.startDate}
            onChange={e => setAddDebtData({ ...addDebtData, startDate: e.target.value })}
            required
          />
          <FormInput
            label="Final Due Date"
            type="date"
            value={addDebtData.dueDate}
            onChange={e => setAddDebtData({ ...addDebtData, dueDate: e.target.value })}
            required
          />
          <FormInput
            label="Amount Repaid"
            type="number"
            value={addDebtData.amountRepaid}
            onChange={e => setAddDebtData({ ...addDebtData, amountRepaid: e.target.value })}
            min="0"
            step="0.01"
            placeholder="0"
          />
          <FormInput
            label="Monthly Payment"
            type="number"
            value={addDebtData.monthlyPayment}
            onChange={e => setAddDebtData({ ...addDebtData, monthlyPayment: e.target.value })}
            min="0"
            step="0.01"
          />
          
          {/* Payment Schedule Form for Add */}
          <PaymentScheduleForm 
            data={addDebtData} 
            setData={setAddDebtData}
          />
          
          {/* Auto-populated Monthly Payment Due Date Field */}
          {/* <FormInput
            label="Monthly Payment Due Date"
            type="date"
            value={addDebtData.monthlyPaymentDueDate}
            onChange={e => setAddDebtData({ ...addDebtData, monthlyPaymentDueDate: e.target.value })}
            className="bg-gray-50"
          /> */}
          
          <FormSelect
            label="Status"
            value={addDebtData.status}
            onChange={e => setAddDebtData({ ...addDebtData, status: e.target.value })}
            required
          >
            <option value="">Select Status</option>
            {DEBT_STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Payment Account"
            value={addDebtData.paymentAccountId}
            onChange={e => setAddDebtData({ ...addDebtData, paymentAccountId: e.target.value })}
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name}</option>
            ))}
          </FormSelect>
          <FormTextarea
            label="Notes"
            value={addDebtData.notes}
            onChange={e => setAddDebtData({ ...addDebtData, notes: e.target.value })}
            rows={2}
            placeholder="Add notes about your debt (optional)"
          />
        </div>
      </EditModal>

      <GenericDetailsModal
        show={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedDebt(null);
        }}
        item={selectedDebt}
        onUpdateItem={handleUpdateDebt}
        onAddTransaction={handleAddPayment}
        baseCurrency={baseCurrency}
        accounts={accounts}
        isUpdating={isUpdating}
        modalType="debt"
      />

      <InstructionsModal
        show={showInstructions}
        onClose={() => setShowInstructions(false)}
        type="debts"
        onDownloadTemplate={handleDownloadTemplate}
      />
    </div>
  );
};

export default DebtContent;